import React, { useState } from "react";
import { ChevronDown, ChevronRight, Plus, Trash2, Lightbulb, StickyNote } from "lucide-react";
import ItemHelpButton from "./ItemHelpButton";
import { porchMathHint } from "./PorchCeilingsCard";
import { fmt } from "@/lib/api";
import { useT, useLang } from "@/lib/i18n";
import { tSection, tItem, tUnit, translateNote } from "@/lib/catalogTranslations";
import { isCommonOnTab, unfilledCommonCount } from "@/lib/commonItems";
import { groupLinesBySubCategory } from "@/lib/subCategories";

/**
 * Renders one collapsible section.
 * Categories matching "Misc. Labor Only" and "Misc. Labor & Material" also
 * render a list of ad-hoc rows (misc_labor / misc_material).
 */
const MISC_LABOR_SECTION = "Misc. Labor Only";
const MISC_MATERIAL_SECTION = "Misc. Labor & Material";

// Sections (other than the dedicated Misc sections above) that opt-in to
// editable "Add custom line" rows. The custom rows piggy-back on the
// existing `misc_material` storage but are tagged with `section` so they
// render under the opted-in section instead of Misc. Labor & Material.
//
// Currently: Window Installation only. Add more section titles here when
// contractors want freeform line items anywhere else.
const CUSTOM_LINE_SECTIONS = new Set([
  "Window Installation",
]);

// Whitelist of catalog items whose Material price the contractor can edit
// inline (just like Labor). Most items use the catalog's fixed price, but
// some (e.g. "Window Package Price") are job-specific bundles that need
// to be priced per quote.
const EDITABLE_MAT_ITEMS = new Set([
  "Vero Window Quote",
  "Vero - Sliding glass door Custom Size",
]);

// R2 follow-on GO (Howard 2026-07-30): coil-family rows carry the colour
// of the component they wrap, from Job Info MATERIAL COLORS. Manual rows
// (PVC/G8) have no emitter, so the chip lives here at the display layer —
// a note cannot ride a line that is never emitted.
const COIL_COLOUR_ROWS = new Set([".019 Coil", "PVC Trim Coil", "Performance G8 Trim Coil"]);
function coilColourFor(sectionTitle, name, est) {
  if (!COIL_COLOUR_ROWS.has(name)) return null;
  if (sectionTitle === "Siding Accessories")
    return { label: "wraps openings — window-wrap colour", value: est?.window_wrap_color };
  if (sectionTitle === "Vinyl Soffit with Siding")
    return { label: "fascia wrap — soffit/fascia colour", value: est?.soffit_fascia_color };
  return null;
}

export default function SectionAccordion({
  section,
  lines,
  isOpen,
  onToggle,
  onQty,
  onField,
  onResetLine,
  // SEND-298d — the way back off a typed qty, on the row that locked.
  onUseTakeoffQty,
  derivedUnapplied,
  onToggleAdder,
  onUpdateAdderQty,
  est,
  update,
  activeTab = "vinyl",
  // Estimate consolidation (ruled 2026-07-24): derivation notes from the
  // LP package render as provenance chips on the matching tab line.
  provenance = null,
}) {
  const t = useT();
  const { lang } = useLang();
  const isMiscLab = section.title === MISC_LABOR_SECTION;
  const isMiscMat = section.title === MISC_MATERIAL_SECTION;
  const allowsCustomLines = CUSTOM_LINE_SECTIONS.has(section.title);
  // Whether to show the Material column on the editable misc rows. True
  // for the dedicated "Misc. Labor & Material" section AND any
  // CUSTOM_LINE_SECTIONS (Window Installation, etc.). "Misc. Labor Only"
  // shows labor only.
  const showMatCol = section.title === MISC_MATERIAL_SECTION || CUSTOM_LINE_SECTIONS.has(section.title);
  // For Misc sections, miscKey is the legacy storage key. For
  // CUSTOM_LINE_SECTIONS we use the same misc_material storage so all the
  // editable mat+lab+desc + add/remove plumbing is shared.
  const miscKey = isMiscLab
    ? "misc_labor"
    : isMiscMat || allowsCustomLines
    ? "misc_material"
    : null;
  // Misc rows are scoped per tab — each tab keeps its own custom labor /
  // custom material entries so multi-product quotes don't bleed into each
  // other. CUSTOM_LINE_SECTIONS are additionally scoped by `section` so
  // a Window-Installation custom line doesn't leak into the dedicated
  // Misc. Labor & Material section (or vice-versa).
  const allMiscRows = miscKey ? est[miscKey] || [] : [];
  const miscRows = allMiscRows.filter((r) => {
    if ((r.tab || "vinyl") !== activeTab) return false;
    const rowSection = r.section || "";
    if (allowsCustomLines) {
      return rowSection === section.title;
    }
    // Legacy Misc sections claim rows with no section tag (back-compat)
    // OR rows explicitly tagged with this misc title.
    return !rowSection || rowSection === section.title;
  });

  const sectionSell =
    // Iter 36: include selected adders (with their own qty) in the
    // section header total so toggling/qty'ing an upgrade visibly bumps
    // the section subtotal.
    lines.reduce((sum, l) => {
      const adders = Array.isArray(l.adders) ? l.adders : [];
      const aMat = adders.reduce((s, a) => s + (Number(a.qty) || 0) * (Number(a.mat) || 0), 0);
      const aLab = adders.reduce((s, a) => s + (Number(a.qty) || 0) * (Number(a.lab) || 0), 0);
      return sum + (l.qty || 0) * ((l.mat || 0) + (l.lab || 0)) + aMat + aLab;
    }, 0) +
    miscRows.reduce((s, m) => s + (m.mat || 0) + (m.lab || 0), 0);

  const filledCount = lines.filter((l) => (l.qty || 0) > 0).length + miscRows.length;
  // Yellow flag pill: shown on the collapsed section header so contractors
  // know which categories have commonly-needed items they haven't quoted yet.
  // Tab-scoped so the Ascend tab doesn't show flags for vinyl-style
  // accessories that aren't really "common" on that tab.
  const unfilledCommon = unfilledCommonCount(lines, activeTab);

  const addMisc = () => {
    // CUSTOM_LINE_SECTIONS always emit material+labor rows (Window Install
    // contractors want to bill both mat and lab for upcharges); legacy
    // Misc. Labor & Material keeps the same shape. Misc. Labor Only emits
    // a labor-only row. Section tag stamped so filtering above stays
    // correct when contractors flip between tabs/sections.
    const wantsMat = isMiscMat || allowsCustomLines;
    const newRow = wantsMat
      ? {
          _id: crypto.randomUUID(),
          desc: "",
          mat: 0,
          lab: 0,
          tab: activeTab,
          section: section.title,
        }
      : {
          _id: crypto.randomUUID(),
          desc: "",
          lab: 0,
          tab: activeTab,
          section: section.title,
        };
    update({ [miscKey]: [...allMiscRows, newRow] });
  };
  const updateMisc = (idx, key, val) => {
    // `idx` is an index into the FILTERED (tab-scoped) rows. Translate it
    // back to the full array before mutating so other tabs' misc rows are
    // not disturbed.
    const target = miscRows[idx];
    const next = allMiscRows.map((r) =>
      r === target ? { ...r, [key]: key === "desc" ? val : Number(val) || 0 } : r
    );
    update({ [miscKey]: next });
  };
  const removeMisc = (idx) => {
    const target = miscRows[idx];
    update({ [miscKey]: allMiscRows.filter((r) => r !== target) });
  };

  // Sub-category support (currently only Vinyl Siding splits by brand).
  // openSubs tracks which nested drop-downs are expanded; defaults to the
  // Charter Oak brand expanded since it's the most-quoted siding line.
  const subGroups = groupLinesBySubCategory(section.title, lines);
  const [openSubs, setOpenSubs] = useState(() => {
    if (!subGroups) return new Set();
    // Prefer Charter Oak when present (Vinyl Siding); otherwise fall back
    // to the first sub-category so the section is never visually empty.
    const charter = subGroups.find((g) => g.id === "charter");
    return new Set([(charter || subGroups[0]).id]);
  });
  const toggleSub = (id) =>
    setOpenSubs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Iter 36: track which window lines have their adder block expanded.
  // Keyed by `${l.tab}::${l.name}` so multiple lines with the same name
  // across tabs stay independent.
  const [openAdders, setOpenAdders] = useState(() => new Set());
  // BLIND-ROW NOTES (ruled 2026-07-31): per-row contractor note editor
  // open-state. The note is the ONLY annotation the hand-filled manual
  // rows have — it prints on the material list.
  const [openNotes, setOpenNotes] = useState(() => new Set());
  const toggleNoteOpen = (key) =>
    setOpenNotes((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  const toggleAddersOpen = (key) =>
    setOpenAdders((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const renderLine = (l) => {
    // Iter 36: per-line adders now carry their OWN qty (a 10-window
    // line can have only 3 tempered glass etc). Subtotals = base
    // line.qty * (mat+lab) PLUS adder.qty * (mat+lab) per selected adder.
    const lineAdders = Array.isArray(l.adders) ? l.adders : [];
    const adderMatSubtotal = lineAdders.reduce(
      (s, a) => s + (Number(a.qty) || 0) * (Number(a.mat) || 0), 0
    );
    const adderLabSubtotal = lineAdders.reduce(
      (s, a) => s + (Number(a.qty) || 0) * (Number(a.lab) || 0), 0
    );
    const total = (l.qty || 0) * ((l.mat || 0) + (l.lab || 0)) + adderMatSubtotal + adderLabSubtotal;
    const labOverridden = l.defaultLab != null && Number(l.lab) !== Number(l.defaultLab);
    const isCommon = isCommonOnTab(l.name, activeTab);
    // The catalog ships per-section adders for window-product sections.
    // Only show the toggle row when the section actually has adders and
    // the line has a qty (no point picking upgrades on an empty row).
    const sectionAdders = Array.isArray(section.adders) ? section.adders : [];
    const showAdderUI = sectionAdders.length > 0 && (l.qty || 0) > 0;
    const adderKey = `${l.tab}::${l.name}`;
    const isAdderOpen = openAdders.has(adderKey);
    const isNoteOpen = openNotes.has(adderKey);
    // Map of adder.name -> saved entry so we can read qty cheaply.
    const selectedByName = new Map(lineAdders.map((a) => [a.name, a]));
    return (
      <React.Fragment key={adderKey}>
      <div
        className={`grid grid-cols-12 gap-3 px-4 md:px-5 py-3 md:py-2 border-b border-[var(--border)] items-center ${
          isCommon ? "bg-[var(--hint-bg)]" : ""
        }`}
        data-testid={`row-${section.title}-${l.name}`}
      >
        <div className="col-span-12 md:col-span-5">
          <div className="text-sm font-semibold md:font-normal text-[var(--ink)] flex items-center gap-2 flex-wrap">
            {isCommon && (
              <Lightbulb
                className="w-3.5 h-3.5 text-[var(--hint-ink)] flex-shrink-0"
                title="Commonly needed — review qty"
              />
            )}
            {tItem(l.name, lang)}
            <ItemHelpButton itemName={l.name} />
            {l.ami_part && (
              <span
                className="ml-2 inline-block font-mono-num text-[10px] tracking-wider text-[var(--muted)] bg-[var(--bg-app)] border border-[var(--border)] px-1.5 py-0.5 rounded-sm"
                title="Alside part number"
                data-testid={`ami-${section.title}-${l.name}`}
              >
                AMI #{l.ami_part}
              </span>
            )}
            {provenance?.[l.name] && (
              <details className="inline-block align-middle" data-testid={`prov-chip-${section.title}-${l.name}`}>
                <summary className="cursor-pointer list-none inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-bold border border-[var(--border)] bg-[var(--bg-app)] text-[var(--muted)]" title="Derivation provenance — click to expand">
                  ⌁ {t("est.derivedChip")}
                </summary>
                <div className="text-[10px] text-[var(--muted)] mt-1 max-w-xl whitespace-pre-wrap font-normal normal-case tracking-normal">
                  {translateNote(provenance[l.name], lang)}
                </div>
              </details>
            )}
            {l.qty_src === "human" && (
              <span
                className="inline-flex items-center px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-bold border border-sky-300 bg-sky-50 text-sky-800"
                title="Hand-typed quantity — survives profile rebuilds/restores verbatim"
                data-testid={`prov-human-${section.title}-${l.name}`}
              >
                human qty
              </span>
            )}
            {/* SEND-298d (Howard ruled 2026-08-14) — USE TAKEOFF QTY: THE WAY
                BACK off an accidental typed quantity. "One click drops the
                typed qty and the next Apply All writes the takeoff number
                again." It sits ON the row, beside the lock it releases, and
                it never deletes the line or asks him to redraw anything. */}
            {l.qty_src === "human" && onUseTakeoffQty && (
              <button
                type="button"
                onClick={() => onUseTakeoffQty(l.section, l.name)}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-bold border border-[var(--ink)] bg-[var(--bg-app)] text-[var(--ink)] hover:bg-[var(--hint-bg)]"
                title="Drop this typed quantity and go back to the photo-takeoff number. The next Apply All Photos writes it again from the marks still on the photos. Nothing is deleted and nothing is redrawn."
                data-testid={`use-takeoff-qty-${section.title}-${l.name}`}
              >
                ↺ Use takeoff qty
              </button>
            )}
            {l.qty_src === "human" && l.derived_qty != null &&
              Number(l.derived_qty) !== Number(l.qty) && (
              <span
                className="inline-flex items-center px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-bold border border-violet-300 bg-violet-50 text-violet-800"
                title="Your typed quantity survives every re-derive — the derivation's fresh number is shown for comparison"
                data-testid={`yours-derived-${section.title}-${l.name}`}
              >
                yours: {l.qty} · derived: {l.derived_qty}
              </span>
            )}
            {l.lab_src === "pending" && (
              <span
                className="inline-flex items-center px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-bold border border-amber-400 bg-amber-50 text-amber-800"
                title="Labor is yours to set — $0 until you enter your rate. Type it here (it becomes your company standing rate) or fill the Price Catalog LABOR column."
                data-testid={`pending-labor-${section.title}-${l.name}`}
              >
                contractor sets labor
              </span>
            )}
            {(() => {
              const cc = coilColourFor(section.title, l.name, est);
              if (!cc) return null;
              return (
                <span
                  className={`inline-flex items-center px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-bold border ${cc.value ? "border-[var(--border)] bg-[var(--bg-app)] text-[var(--ink-2)]" : "border-amber-400 bg-amber-50 text-amber-800"}`}
                  title="Colour of the component this coil wraps — from Job Info MATERIAL COLORS"
                  data-testid={`coil-colour-${section.title}-${l.name}`}
                >
                  {cc.value ? `${cc.label}: ${cc.value}` : "colour not set — set in Job Info"}
                </span>
              );
            })()}
            <button
              type="button"
              onClick={() => toggleNoteOpen(adderKey)}
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-bold border transition-colors ${
                l.contractor_note
                  ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                  : "border-[var(--border)] bg-[var(--bg-app)] text-[var(--muted)] hover:text-[var(--ink)]"
              }`}
              title={t("est.note.prints")}
              data-testid={`contractor-note-btn-${section.title}-${l.name}`}
            >
              <StickyNote className="w-3 h-3" />
              {l.contractor_note ? t("est.note.chip") : t("est.note.add")}
            </button>
          </div>
        </div>
        <div className="col-span-3 md:col-span-1 text-xs text-[var(--muted)] uppercase tracking-wider">
          <span className="md:hidden text-[10px] text-[var(--muted)] block">{t("est.col.unit")}</span>
          {tUnit(l.unit, lang)}
        </div>
        <div className="col-span-3 md:col-span-2 text-right text-sm font-mono-num text-[var(--ink-2)]">
          <span className="md:hidden text-[10px] text-[var(--muted)] block text-right">{t("est.col.mat")}</span>
          {/* Iter 78z++++ — Mat $ is editable for every catalog row inside
              the merged "Misc. Labor and Material" section so the
              contractor can override per-job material cost without
              touching the supplier catalog. Falls back to the
              EDITABLE_MAT_ITEMS whitelist for the few job-specific
              custom lines (Vero Window Quote, etc.). */}
          {(isMiscMat || EDITABLE_MAT_ITEMS.has(l.name)) ? (
            <div className="relative">
              <input
                className={`input num h-11 md:h-9 text-base md:text-sm w-full text-right ${
                  l.defaultMat != null && Number(l.mat) !== Number(l.defaultMat)
                    ? "border-[var(--brand)] bg-orange-50"
                    : ""
                }`}
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                value={l.mat ?? 0}
                onChange={(e) => onField(l.tab, l.section, l.name, "mat", e.target.value)}
                data-testid={`mat-${section.title}-${l.name}`}
              />
              {l.defaultMat != null && Number(l.mat) !== Number(l.defaultMat) && (
                <button
                  type="button"
                  className="absolute -top-1 -right-1 w-5 h-5 md:w-4 md:h-4 rounded-full bg-[var(--brand)] text-[var(--on-brand)] text-xs md:text-[10px] leading-none flex items-center justify-center"
                  onClick={() =>
                    onField(l.tab, l.section, l.name, "mat", l.defaultMat ?? 0)
                  }
                  title={`Reset to catalog default ($${l.defaultMat ?? 0})`}
                  data-testid={`reset-mat-${section.title}-${l.name}`}
                >
                  ↺
                </button>
              )}
            </div>
          ) : (
            fmt(l.mat)
          )}
        </div>
        <div className="col-span-3 md:col-span-1">
          <label className="md:hidden text-[10px] text-[var(--muted)] block uppercase tracking-wider mb-1">{t("est.col.qty")}</label>
          <input
            className="input num h-11 md:h-9 text-base md:text-sm w-full"
            type="number"
            inputMode="decimal"
            step="0.5"
            min="0"
            value={l.qty || ""}
            placeholder="0"
            // Wheel-over-a-number-input silently mutates qty and stamps a
            // phantom qty_src=human (Casile forensic item, 2026-07-24) —
            // blur kills the browser's scroll-to-spin footgun.
            onWheel={(e) => e.currentTarget.blur()}
            onChange={(e) => onQty(l.tab, l.section, l.name, e.target.value)}
            data-testid={`qty-${section.title}-${l.name}`}
          />
          {/* SEND-226 (Howard ruled 2026-08-14) — SHOW DERIVED ON THE ROW.
              The QTY box stays what is BILLED (his own number when he typed
              one); the photo's figure sits beside it in plain text with its
              unit, on the same row. A derived figure stored and not rendered
              is the bug — he could not see 3.7 / 36 / 3 anywhere. No money
              moves onto it while the QTY is his. */}
          {l.derived_qty != null && Number(l.derived_qty) !== Number(l.qty) && (
            <div className="text-[10px] text-[var(--muted)] mt-1 leading-snug font-mono-num"
              title="The photo takeoff's derived figure — your typed QTY is what bills"
              data-testid={`derived-beside-${section.title}-${l.name}`}>
              photo {l.derived_qty} {l.unit}
            </div>
          )}
          {/* Iter 79 — inline Porch Ceiling math hint. Shows the per-porch
              derivation (e.g. "22'×10' + 12'×8' = 316 sqft → 32 pcs") so
              the contractor can verify the auto-populated qty without
              opening the Porch Ceilings panel. Only renders on the two
              Porch Ceiling rows when at least one porch with valid
              L×W dimensions exists. */}
          {section.title === "Porch Ceiling" && (l.name === "Charter Oak Soffit White" || l.name === "Wrap porch beam") && (() => {
            const hint = porchMathHint(est?.porch_ceilings, l.name === "Wrap porch beam" ? "lf" : "sqft");
            return hint ? (
              <div
                className="hidden md:block text-[10px] text-[var(--muted)] mt-1 leading-snug font-mono-num"
                data-testid={`porch-math-hint-${l.name}`}
                title={hint}
              >
                {hint}
              </div>
            ) : null;
          })()}
        </div>
        <div className="col-span-3 md:col-span-1 relative">
          <label className="md:hidden text-[10px] text-[var(--muted)] block uppercase tracking-wider mb-1">{t("est.col.lab")}</label>
          {/* Iter 78k (2026-02-25): Howard reversed the Iter 69 vinyl/ascend
              labor lockdown — labor is now editable on ALL siding tabs
              (vinyl, ascend, lp_smart) and windows. Single editable input
              with override styling shared across every tab. */}
          <>
            <input
              className={`input num h-11 md:h-9 text-base md:text-sm w-full ${labOverridden ? "border-[var(--brand)] bg-orange-50" : ""}`}
              type="number"
              inputMode="decimal"
              step="0.25"
              min="0"
              value={l.lab ?? 0}
              onChange={(e) => onField(l.tab, l.section, l.name, "lab", e.target.value)}
              title={labOverridden ? `Catalog default: $${l.defaultLab}` : ""}
              data-testid={`lab-${section.title}-${l.name}`}
            />
            {labOverridden && (
              <button
                type="button"
                className="absolute -top-1 -right-1 w-5 h-5 md:w-4 md:h-4 rounded-full bg-[var(--brand)] text-[var(--on-brand)] text-xs md:text-[10px] leading-none flex items-center justify-center"
                onClick={() => onResetLine(l.tab, l.section, l.name)}
                title={`Reset to catalog default ($${l.defaultLab})`}
                data-testid={`reset-lab-${section.title}-${l.name}`}
              >
                ↺
              </button>
            )}
          </>
        </div>
        <div className="col-span-12 md:col-span-2 text-right font-mono-num text-base md:text-sm font-bold md:font-semibold text-[var(--ink)] pt-2 md:pt-0 border-t md:border-t-0 border-[var(--bg-app)]">
          <span className="md:hidden text-[10px] text-[var(--muted)] uppercase tracking-wider mr-2">{t("est.col.total")}</span>
          {fmt(total)}
        </div>
      </div>
      {(isNoteOpen || (l.contractor_note || "").trim()) && (
        <div
          className="border-b border-[var(--border)] bg-[var(--bg-app)] px-4 md:px-5 py-2"
          data-testid={`contractor-note-block-${section.title}-${l.name}`}
        >
          {isNoteOpen ? (
            <div>
              <textarea
                className="input w-full text-sm leading-snug min-h-[54px] py-1.5"
                maxLength={500}
                placeholder={t("est.note.placeholder")}
                value={l.contractor_note || ""}
                onChange={(e) => onField(l.tab, l.section, l.name, "contractor_note", e.target.value)}
                data-testid={`contractor-note-input-${section.title}-${l.name}`}
              />
              <div className="text-[10px] text-[var(--muted)] mt-0.5">
                ✎ {t("est.note.prints")}
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => toggleNoteOpen(adderKey)}
              className="text-left w-full text-[11px] text-[var(--ink-2)] leading-snug"
              title={t("est.note.prints")}
              data-testid={`contractor-note-text-${section.title}-${l.name}`}
            >
              ✎ {l.contractor_note}
            </button>
          )}
        </div>
      )}
      {showAdderUI && (
        <div className="border-b border-[var(--border)] bg-[var(--surface-muted)]" data-testid={`adder-block-${l.name}`}>
          <button
            type="button"
            onClick={() => toggleAddersOpen(adderKey)}
            className="w-full flex items-center justify-between px-4 md:px-5 py-2 text-left text-[11px] uppercase tracking-[0.18em] text-[var(--ink-2)] hover:text-[var(--ink)]"
            data-testid={`adder-toggle-${l.name}`}
          >
            <span className="flex items-center gap-2">
              {isAdderOpen ? (
                <ChevronDown className="w-3.5 h-3.5" />
              ) : (
                <ChevronRight className="w-3.5 h-3.5" />
              )}
              Upgrade Options
              {lineAdders.length > 0 && (
                <span className="bg-[var(--brand)] text-[var(--on-brand)] px-2 py-0.5 text-[10px] tracking-wider font-bold normal-case">
                  {lineAdders.length}
                </span>
              )}
            </span>
            <span className="font-mono-num text-[11px] text-[var(--ink-2)] normal-case tracking-normal">
              {adderMatSubtotal + adderLabSubtotal > 0
                ? `+${fmt(adderMatSubtotal + adderLabSubtotal)}`
                : ""}
            </span>
          </button>
          {isAdderOpen && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 px-4 md:px-8 pb-3 pt-1">
              {sectionAdders.map((a) => {
                const selected = selectedByName.get(a.name);
                const checked = !!selected;
                const adderQty = checked ? Number(selected.qty) || 0 : 0;
                const unitCost = (Number(a.mat) || 0) + (Number(a.lab) || 0);
                const adderTotal = adderQty * unitCost;
                return (
                  <div
                    key={a.name}
                    className={`flex items-center gap-2.5 py-1.5 border-b border-[#EDEDF0] last:border-b-0 text-[13px] ${
                      checked ? "text-[var(--ink)] font-semibold" : "text-[#3F3F46]"
                    }`}
                    data-testid={`adder-option-${l.name}-${a.name}`}
                  >
                    <input
                      type="checkbox"
                      className="w-4 h-4 accent-[var(--brand)] flex-shrink-0 cursor-pointer"
                      checked={checked}
                      onChange={() =>
                        onToggleAdder && onToggleAdder(l.tab, l.section, l.name, a)
                      }
                      data-testid={`adder-checkbox-${l.name}-${a.name}`}
                    />
                    <span className="flex-1 leading-snug">{a.name}</span>
                    {checked ? (
                      <>
                        <input
                          type="number"
                          inputMode="decimal"
                          min="0"
                          step="1"
                          value={adderQty || ""}
                          placeholder={`${Number(l.qty) || 0}`}
                          onChange={(ev) =>
                            onUpdateAdderQty &&
                            onUpdateAdderQty(l.tab, l.section, l.name, a.name, ev.target.value)
                          }
                          className="input num h-7 text-xs w-14 text-right"
                          title={`Of ${Number(l.qty) || 0} ${l.name.includes("Door") ? "doors" : "windows"}, how many get this`}
                          data-testid={`adder-qty-${l.name}-${a.name}`}
                        />
                        <span className="font-mono-num text-[11px] text-[var(--muted)] whitespace-nowrap w-16 text-right">
                          {adderTotal > 0 ? `+${fmt(adderTotal)}` : "—"}
                        </span>
                      </>
                    ) : (
                      <span className="font-mono-num text-[11px] text-[var(--muted)] whitespace-nowrap">
                        {unitCost > 0 ? `+${fmt(unitCost)}/ea` : "—"}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      </React.Fragment>
    );
  };

  return (
    <section className="card mb-4" data-testid={`section-${section.title}`}>
      <button
        className="w-full flex items-center justify-between px-5 py-4 text-left"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <span className="section-tag">{tSection(section.title, lang)}</span>
          {filledCount > 0 && (
            <span className="text-[10px] font-bold px-2 py-0.5 border border-[var(--brand)] text-[var(--brand-text)]">
              {t("est.itemsBadge", { n: filledCount })}
            </span>
          )}
          {unfilledCommon > 0 && (
            <span
              className="text-[10px] font-bold px-2 py-0.5 bg-[var(--hint-bg-2)] border border-[var(--hint-line)] text-[var(--hint-ink-2)] flex items-center gap-1"
              title="Commonly-needed items in this section haven't been quoted yet"
              data-testid={`common-flag-${section.title}`}
            >
              <Lightbulb className="w-3 h-3" />
              {unfilledCommon}
            </span>
          )}
        </div>
        {derivedUnapplied && sectionSell === 0 ? (
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#92400E]" data-testid="section-derived-unapplied">
            Derived — not applied
          </div>
        ) : (
          <div className="font-mono-num text-sm text-[var(--ink-2)]">{fmt(sectionSell)}</div>
        )}
      </button>
      {isOpen && (
        <div className="border-t border-[var(--border)]">
          <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-2 text-[10px] uppercase tracking-[0.18em] text-[var(--muted)] font-bold border-b border-[var(--border)]">
            <div className="col-span-5">{t("est.col.item")}</div>
            <div className="col-span-1">{t("est.col.unit")}</div>
            <div className="col-span-2 text-right">{t("est.col.mat")}</div>
            <div className="col-span-1 text-right">{t("est.col.qty")}</div>
            <div className="col-span-1 text-right">{t("est.col.lab")}</div>
            <div className="col-span-2 text-right">{t("est.col.total")}</div>
          </div>
          {subGroups
            ? subGroups.map((g) => {
                const isOpenSub = openSubs.has(g.id);
                const subFilled = g.lines.filter((l) => (l.qty || 0) > 0).length;
                const subTotal = g.lines.reduce(
                  (s, l) => s + (l.qty || 0) * ((l.mat || 0) + (l.lab || 0)),
                  0
                );
                const subUnfilled = g.lines.filter(
                  (l) => isCommonOnTab(l.name, activeTab) && (l.qty || 0) <= 0
                ).length;
                return (
                  <div key={g.id} className="border-b border-[var(--border)]" data-testid={`subcat-${g.id}`}>
                    <button
                      type="button"
                      onClick={() => toggleSub(g.id)}
                      className="w-full flex items-center justify-between px-5 py-2.5 text-left bg-[var(--surface-muted)] hover:bg-[var(--bg-app)]"
                      data-testid={`subcat-toggle-${g.id}`}
                    >
                      <div className="flex items-center gap-2.5">
                        {isOpenSub ? (
                          <ChevronDown className="w-3.5 h-3.5 text-[var(--muted)]" />
                        ) : (
                          <ChevronRight className="w-3.5 h-3.5 text-[var(--muted)]" />
                        )}
                        <span className="text-xs uppercase tracking-[0.14em] font-semibold text-[#3F3F46]">
                          {g.label}
                        </span>
                        <span className="text-[10px] text-[var(--muted)] font-mono-num">
                          {g.lines.length}
                        </span>
                        {subFilled > 0 && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 border border-[var(--brand)] text-[var(--brand-text)]">
                            {t("est.itemsBadge", { n: subFilled })}
                          </span>
                        )}
                        {subUnfilled > 0 && (
                          <span
                            className="text-[10px] font-bold px-1.5 py-0.5 bg-[var(--hint-bg-2)] border border-[var(--hint-line)] text-[var(--hint-ink-2)] flex items-center gap-1"
                            title="Commonly-needed items in this sub-category haven't been quoted yet"
                          >
                            <Lightbulb className="w-3 h-3" />
                            {subUnfilled}
                          </span>
                        )}
                      </div>
                      <div className="font-mono-num text-xs text-[var(--ink-2)]">{fmt(subTotal)}</div>
                    </button>
                    {isOpenSub && <div>{g.lines.map(renderLine)}</div>}
                  </div>
                );
              })
            : lines.map(renderLine)}

          {/* Misc / ad-hoc rows */}
          {miscKey && (
            <>
              {miscRows.length > 0 && (
                <div className="hidden md:grid grid-cols-12 gap-3 px-5 pt-3 pb-1 text-[10px] uppercase tracking-[0.18em] text-[var(--muted)] font-bold">
                  <div className="col-span-6">{t("est.customDesc")}</div>
                  {showMatCol && <div className="col-span-2 text-right">{t("cat.col.material")}</div>}
                  <div className={`col-span-${showMatCol ? 2 : 4} text-right`}>{t("cat.col.labor")}</div>
                  <div className="col-span-2 text-right">{t("est.col.total")}</div>
                </div>
              )}
              {miscRows.map((r, i) => {
                const rowTotal = (r.mat || 0) + (r.lab || 0);
                return (
                  <div
                    key={r._id || i}
                    className="grid grid-cols-12 gap-3 px-5 py-2 border-b border-[var(--border)] items-center bg-[var(--surface-muted)]"
                    data-testid={`misc-row-${section.title}-${i}`}
                  >
                    <input
                      className="input col-span-12 md:col-span-6"
                      value={r.desc}
                      placeholder={t("est.customDescPlaceholder")}
                      onChange={(e) => updateMisc(i, "desc", e.target.value)}
                      data-testid={`misc-desc-${section.title}-${i}`}
                    />
                    {showMatCol && (
                      <input
                        className="input num col-span-4 md:col-span-2"
                        type="number"
                        step="0.01"
                        value={r.mat}
                        onChange={(e) => updateMisc(i, "mat", e.target.value)}
                        data-testid={`misc-mat-${section.title}-${i}`}
                      />
                    )}
                    <input
                      className={`input num col-span-${showMatCol ? 4 : 8} md:col-span-${showMatCol ? 2 : 4}`}
                      type="number"
                      step="0.01"
                      value={r.lab}
                      onChange={(e) => updateMisc(i, "lab", e.target.value)}
                      data-testid={`misc-lab-${section.title}-${i}`}
                    />
                    <div className="col-span-3 md:col-span-1 text-right font-mono-num text-sm font-semibold text-[var(--ink)]">
                      {fmt(rowTotal)}
                    </div>
                    <button
                      className="btn-danger col-span-1 justify-self-end"
                      onClick={() => removeMisc(i)}
                      aria-label={t("est.removeMisc")}
                      data-testid={`misc-remove-${section.title}-${i}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
              <div className="px-5 py-3">
                <button
                  className="btn-ghost border border-dashed border-[var(--muted)] hover:border-[var(--border-strong)] hover:text-[var(--ink)] w-full justify-center"
                  onClick={addMisc}
                  data-testid={`add-misc-${section.title}`}
                >
                  <Plus className="w-4 h-4" /> {t("est.addCustomLine")}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}
